import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type DataMessageType,
  type TerraUIMessage,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import type { SkillDraft } from '../../store/skillDraftsStore';
import { readObjectValue } from '../../utils/readObjectValue';

export type SkillDraftUpdate = Partial<SkillDraft> & {
  id: string;
  reason: string;
};

const MAX_DRAFT_UPDATE_PARSE_DEPTH = 8;

export function readSkillDraftUpdates({
  messages,
}: {
  messages: TerraUIMessage[];
}): SkillDraftUpdate[] {
  const updates: SkillDraftUpdate[] = [];

  for (const message of messages) {
    for (const part of message.parts) {
      const update = readSkillDraftUpdate(part, message.id);

      if (update) {
        updates.push(update);
      }
    }
  }

  return updates;
}

function readSkillDraftUpdate(
  part: TerraUIMessage['parts'][number],
  messageId: string,
): SkillDraftUpdate | null {
  const sdkToolUpdate = readSdkToolDraftUpdate(part, messageId);

  if (sdkToolUpdate) {
    return sdkToolUpdate;
  }

  if (part.type !== TerraUIMessageDataPartName) {
    return null;
  }

  const data = part.data;

  if (!data || !isSkillBuilderToolCallData(data)) {
    return null;
  }

  const parsedOutput = parseDraftUpdateOutput(data.output);

  if (!parsedOutput) {
    return null;
  }

  return {
    id: `${messageId}:${data.toolCallId}`,
    ...parsedOutput,
  };
}

function isSkillBuilderToolCallData(
  data: DataMessageType,
): data is ToolCallGenericMessage {
  return (
    [
      DataMessageTypeEnum.ToolCallComplete,
      DataMessageTypeEnum.ToolCallUpdate,
      DataMessageTypeEnum.ToolCall,
    ].includes(data.type) &&
    normalizeToolName((data as ToolCallGenericMessage).toolName) ===
      'propose-skill-draft-update'
  );
}

function readSdkToolDraftUpdate(
  part: TerraUIMessage['parts'][number],
  messageId: string,
): SkillDraftUpdate | null {
  if (
    !part.type.startsWith('tool-') ||
    normalizeToolName(part.type.replace(/^tool-/, '')) !==
      'propose-skill-draft-update'
  ) {
    return null;
  }

  const parsedOutput = parseDraftUpdateOutput(
    readObjectValue(part, 'output') ?? readObjectValue(part, 'result'),
  );

  if (!parsedOutput) {
    return null;
  }

  const toolCallId =
    readOptionalString(readObjectValue(part, 'toolCallId')) ??
    readOptionalString(readObjectValue(part, 'id')) ??
    part.type;

  return {
    id: `${messageId}:${toolCallId}`,
    ...parsedOutput,
  };
}

function parseDraftUpdateOutput(
  output: unknown,
  depth = 0,
): Omit<SkillDraftUpdate, 'id'> | null {
  if (depth > MAX_DRAFT_UPDATE_PARSE_DEPTH) {
    return null;
  }

  const value = typeof output === 'string' ? safeJsonParse(output) : output;

  if (!value || typeof value !== 'object') {
    return null;
  }

  const type = readObjectValue(value, 'type');

  if (type !== 'skill_draft_update_proposal') {
    const nestedOutput =
      readObjectValue(value, 'output') ??
      readObjectValue(value, 'result') ??
      readObjectValue(value, 'content');

    if (nestedOutput !== undefined && nestedOutput !== output) {
      return parseDraftUpdateOutput(nestedOutput, depth + 1);
    }
  }

  if (type !== 'skill_draft_update_proposal') {
    return null;
  }

  const update = {
    slug: readOptionalString(readObjectValue(value, 'slug')),
    name: readOptionalString(readObjectValue(value, 'name')),
    description: readOptionalString(readObjectValue(value, 'description')),
    body: readOptionalString(readObjectValue(value, 'body')),
    reason:
      readOptionalString(readObjectValue(value, 'reason')) ??
      'The builder updated the draft.',
  };

  if (!update.slug && !update.name && !update.description && !update.body) {
    return null;
  }

  return update;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
}

function normalizeToolName(value: string | undefined): string {
  return (value ?? '').replace(/_/g, '-').toLowerCase();
}
